/**
 * /learn/readiness-workbook — My Retirement Readiness Workbook
 *
 * A deeper, 14-part companion to the 50-point checklist. This is a
 * long-form workbook — 10 toolboxes, /100 score, action plan, and a
 * Future You letter. Progress persists in the browser via secureStorage.
 * Print-friendly with JSON-LD HowTo schema for SEO/AI indexing.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Printer,
  Sprout,
  RotateCcw,
  Sparkles,
  Wrench,
  Users,
  TreePine,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import useSEO from "@/lib/useSEO";
import QuickAnswer from "@/components/QuickAnswer";
import { secureGetJSON, secureSet, secureRemove } from "@/lib/secureStorage";
import { track, EVENTS } from "@/lib/analytics";
import { appendScore, loadHistory } from "@/lib/scoreHistory";
import { exportWorkbookPDF } from "@/lib/workbookPdfExport";
import ScoreHistoryChart from "@/components/ScoreHistoryChart";
import MilestoneModal from "@/components/readiness/MilestoneModal";
import AdvisorSharePanel from "@/components/readiness/AdvisorSharePanel";
import EncouragementBanner from "@/components/readiness/EncouragementBanner";

const KEY = "centennial_readiness_workbook_v1";

// 10 toolboxes × 10 points each = 100 total
const TOOLBOXES = [
  { key: "vision",       label: "Retirement vision" },
  { key: "budget",       label: "Budget & cash flow" },
  { key: "debt",         label: "Debt management" },
  { key: "emergency",    label: "Emergency savings" },
  { key: "retirement",   label: "Retirement savings" },
  { key: "social",       label: "Social Security" },
  { key: "healthcare",   label: "Healthcare" },
  { key: "investments",  label: "Investments" },
  { key: "taxes",        label: "Taxes & withdrawals" },
  { key: "estate",       label: "Estate & protection" },
];

const TIERS = [
  { min: 80, label: "Strong Foundation",
    detail: "Wonderful work — many toolboxes are already full. Keep them tuned and review as life shifts." },
  { min: 60, label: "Building Momentum",
    detail: "You've built real momentum. A few more toolboxes on the shelf will round out your plan." },
  { min: 40, label: "Important Work Ahead",
    detail: "You've started the workshop. Pick the toolbox that would give you the most peace of mind first." },
  { min: 0,  label: "You've Begun",
    detail: "Showing up is the hard part — and you did it. Every honest check builds strength for Future You." },
];
const tierFor = (s) => TIERS.find((t) => s >= t.min) || TIERS[TIERS.length - 1];

// A single top-5 action item
const emptyAction = { need_to: "", first_step: "", by_when: "" };

const emptyState = {
  meta: { name: "", today: "", target_year: "", target_age: "" },
  vision: {
    retire_age: "",
    years_from_today: "",
    where: [],
    where_notes: "",
    activities: [],
    activities_other: "",
    three_things: ["", "", ""],
    kitchen_table: "",
  },
  money: {
    have: { checking: "", savings: "", emergency: "", k401: "", ira: "", investments: "", other: "" },
    owe:  { mortgage: "", vehicles: "", credit_cards: "", student: "", personal: "", other: "" },
    debt_biggest_concern: "",
    debt_first_action: "",
  },
  monthly: {
    income:  { takehome: "", spouse: "", other: "" },
    expense: { housing: "", utilities: "", food: "", transport: "", insurance: "", healthcare: "", debt: "", other: "" },
    surprising: "",
    could_change: "",
    could_redirect: "",
  },
  emergency: { thousand: "", one_month: "", three: "", six: "", have: "", essentials: "", goal: "", to_build: "", automatic: "" },
  retirement: { accounts: [], balance: "", monthly_contrib: "", pct_income: "", employer_match: "", full_match: "", investigate: "" },
  social: { has_account: "", reviewed_earnings: "", knows_estimate: "", est_early: "", est_full: "", est_70: "", planned_age: "", why: "", need_info: "" },
  healthcare: { pre_medicare_source: [], medicare_flags: [], biggest_question: "" },
  investing: { knows: [], reaction: "" },
  income: {
    sources: { social: "", pension: "", withdrawals: "", rental: "", parttime: "", other: "" },
    spending: { housing: "", food: "", transport: "", healthcare: "", insurance: "", travel: "", giving: "", other: "" },
    plan_state: "",
  },
  taxes: { knows: [], biggest_question: "" },
  estate: { checks: [], last_review: "", next_review: "" },
  life: {
    monday_morning: "",
    people: "",
    learn: "",
    help: "",
    places: "",
    always_wanted: "",
    bucket: { do: "", go: "", learn: "", share: "" },
  },
  scores: Object.fromEntries(TOOLBOXES.map((t) => [t.key, 0])), // 0 / 5 / 10
  actions: [emptyAction, emptyAction, emptyAction, emptyAction, emptyAction],
  one_year_promise: "",
  signature: "",
  signature_date: "",
  future_you: "",
};

// ─────────────────────────── helpers ───────────────────────────

const num = (v) => (v === "" || v == null ? 0 : Number(v) || 0);
const sum = (obj) => Object.values(obj || {}).reduce((a, v) => a + num(v), 0);

// ─────────────────────────── component ─────────────────────────

export default function LearnReadinessWorkbook() {
  useSEO({
    title: "Retirement Readiness Workbook · Free 14-part guided workbook · Centennial",
    description:
      "Free 14-part retirement readiness workbook — build your future one toolbox at a time. Budget, debt, emergency fund, retirement savings, Social Security, healthcare, investments, taxes, estate, and life planning. Score /100.",
    canonicalPath: "/learn/readiness-workbook",
  });

  // JSON-LD HowTo schema so search + AI can index every toolbox.
  useEffect(() => {
    const steps = TOOLBOXES.map((tb, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: `Build the ${tb.label} toolbox`,
      text: `Complete the ${tb.label} section of the retirement readiness workbook and score it 0/5/10.`,
    }));
    const jsonld = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: "My Retirement Readiness Workbook — 14 parts, 10 toolboxes, /100",
      description:
        "A guided workbook to build your retirement future one toolbox at a time. Vision, money today, monthly cash flow, emergency, retirement savings, Social Security, healthcare, investing, income, taxes, protection, life plan, scoring, and top-5 next steps.",
      totalTime: "PT30M",
      step: steps,
      author: { "@type": "Organization", name: "Centennial" },
      publisher: { "@type": "Organization", name: "Centennial", url: "https://mycentennialplan.com" },
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "workbook-jsonld";
    script.text = JSON.stringify(jsonld);
    document.head.appendChild(script);
    return () => document.getElementById("workbook-jsonld")?.remove();
  }, []);

  const [w, setW] = useState(() => secureGetJSON(KEY, emptyState));
  const [history, setHistory] = useState(() => loadHistory());
  useEffect(() => { secureSet(KEY, w); }, [w]);

  // Convenience setters
  const set = (path, value) => {
    setW((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split(".");
      let node = next;
      for (let i = 0; i < parts.length - 1; i++) node = node[parts[i]] = { ...node[parts[i]] };
      node[parts.at(-1)] = value;
      return next;
    });
  };
  const setMulti = (path, value) => {
    setW((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split(".");
      let node = next;
      for (let i = 0; i < parts.length - 1; i++) node = node[parts[i]] = { ...node[parts[i]] };
      const arr = new Set(node[parts.at(-1)] || []);
      arr.has(value) ? arr.delete(value) : arr.add(value);
      node[parts.at(-1)] = [...arr];
      return next;
    });
  };

  // Score / tier
  const total = useMemo(
    () => TOOLBOXES.reduce((a, t) => a + (w.scores[t.key] || 0), 0),
    [w.scores],
  );
  const tier = tierFor(total);
  const pct = Math.min(100, Math.round(total));

  // Append to score history whenever the total changes (debounced by daily key inside appendScore).
  useEffect(() => {
    if (total > 0) setHistory(appendScore("workbook", total, 100));
  }, [total]);

  // Milestone modal — pops the first time the user crosses 60 or 80.
  const [milestone, setMilestone] = useState(null);
  useEffect(() => {
    const seen = w.milestones_seen || {};
    if (total >= 80 && !seen["80"]) {
      setMilestone(80);
    } else if (total >= 60 && !seen["60"]) {
      setMilestone(60);
    }
  }, [total, w.milestones_seen]);

  const closeMilestone = () => {
    setW((prev) => ({
      ...prev,
      milestones_seen: { ...(prev.milestones_seen || {}), [String(milestone)]: true },
    }));
    setMilestone(null);
  };

  // Auto-computed totals for the visible summary panels
  const moneyHave = sum(w.money.have);
  const moneyOwe = sum(w.money.owe);
  const inc = sum(w.monthly.income);
  const exp = sum(w.monthly.expense);
  const surplus = inc - exp;

  const retIncomeMonthly = sum(w.income.sources);
  const retSpendMonthly = sum(w.income.spending);

  const reset = () => {
    if (!window.confirm("Reset the entire workbook? This clears your saved progress on this device.")) return;
    secureRemove(KEY);
    setW(emptyState);
    toast.success("Workbook cleared.");
  };

  const print = () => {
    track(EVENTS.CALCULATOR_RUN, { workbook_score: total });
    window.print();
  };

  const downloadPdf = () => {
    try {
      exportWorkbookPDF(w);
      track(EVENTS.CALCULATOR_RUN, { workbook_pdf_export: true, workbook_score: total });
      toast.success("Downloaded your workbook PDF.");
    } catch (e) {
      console.error(e);
      toast.error("Could not export PDF. Try Print instead.");
    }
  };

  const setAction = (i, key, value) => {
    setW((prev) => ({
      ...prev,
      actions: prev.actions.map((a, idx) => (idx === i ? { ...a, [key]: value } : a)),
    }));
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] print:bg-white">
      <div className="max-w-4xl mx-auto px-6 py-10 print:px-0 print:py-4">
        {/* Header */}
        <div className="print:hidden flex items-center justify-between gap-3 mb-8 flex-wrap">
          <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2B5339]">
            <ArrowLeft size={14} /> All lessons
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/learn/readiness-checklist"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white border border-[#E5E2DC] hover:border-[#2B5339] text-[#5C6B62]"
            >
              Prefer the 5-min checklist? →
            </Link>
            <button
              data-testid="wb-reset"
              type="button" onClick={reset}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white border border-[#E5E2DC] hover:border-[#C26747] hover:text-[#C26747] text-[#5C6B62]"
            >
              <RotateCcw size={12} /> Reset
            </button>
            <button
              data-testid="wb-pdf"
              type="button" onClick={downloadPdf}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#2B5339] text-white hover:bg-[#1C2B23]"
            >
              <Download size={12} /> Download PDF
            </button>
            <button
              data-testid="wb-print"
              type="button" onClick={print}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white border border-[#E5E2DC] hover:border-[#2B5339] text-[#5C6B62]"
            >
              <Printer size={12} /> Print
            </button>
          </div>
        </div>

        {/* Hero */}
        <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-2 inline-flex items-center gap-2">
          <Wrench size={12} /> MyCentennialPlan™ · Future Builder™ workbook
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C2B23] leading-[1.05]">
          My Retirement<br />Readiness Workbook.
        </h1>
        <p className="mt-5 text-lg text-[#5C6B62] leading-relaxed max-w-2xl">
          Build your future — one toolbox at a time. You don&apos;t need to complete everything today.
          Build one toolbox. Put it on the shelf. Then build another.
        </p>

        <QuickAnswer>
          This free workbook documents everything your family would need in one printable place:
          income sources, account locations, insurance policies, key documents, medical wishes,
          and a guided budget. Work through it one section at a time — everything saves privately
          in your browser, and nothing is uploaded to any server.
        </QuickAnswer>

        <EncouragementBanner />

        {/* Meta */}
        <section data-testid="wb-meta" className="mt-8 grid sm:grid-cols-2 gap-4 bg-white border border-[#E5E2DC] rounded-2xl p-6 print:break-inside-avoid">
          <Field label="Name" value={w.meta.name} onChange={(v) => set("meta.name", v)} testid="wb-name" />
          <Field label="Today" type="date" value={w.meta.today} onChange={(v) => set("meta.today", v)} testid="wb-today" />
          <Field label="Target retirement year" type="number" value={w.meta.target_year} onChange={(v) => set("meta.target_year", v)} testid="wb-year" />
          <Field label="Target retirement age" type="number" value={w.meta.target_age} onChange={(v) => set("meta.target_age", v)} testid="wb-age" />
        </section>

        {/* Score panel */}
        <ScorePanel total={total} tier={tier} pct={pct} scores={w.scores} setScore={(k, v) => set(`scores.${k}`, v)} />

        {/* Score history chart — appears once we have 2+ points */}
        <ScoreHistoryChart history={history} activeKind="workbook" />

        {/* PART 1 — Picture Your Retirement */}
        <Part n="1" title="Picture your retirement" intro="Before calculating money, picture the life.">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="I would like to retire at age" type="number" value={w.vision.retire_age} onChange={(v) => set("vision.retire_age", v)} testid="v-age" />
            <Field label="Approximately how many years from today" type="number" value={w.vision.years_from_today} onChange={(v) => set("vision.years_from_today", v)} testid="v-years" />
          </div>
          <CheckGroup label="Where might I live?" options={[
            "Current home","Smaller home","Different city","Different state","Near family",
            "Retirement community","Two locations part-time","Not sure yet",
          ]} selected={w.vision.where} onToggle={(v) => setMulti("vision.where", v)} testidPrefix="v-where" />
          <Field label="My thoughts" value={w.vision.where_notes} onChange={(v) => set("vision.where_notes", v)} textarea />
          <CheckGroup label="What does retirement look like?" options={[
            "Travel","Family time","Grandchildren","Volunteer","Hobbies","Small business",
            "Part-time work","Learn something new","Stay home more","Give back",
          ]} selected={w.vision.activities} onToggle={(v) => setMulti("vision.activities", v)} testidPrefix="v-act" />
          <Field label="Other" value={w.vision.activities_other} onChange={(v) => set("vision.activities_other", v)} />
          <div className="mt-2">
            <div className="text-xs text-[#5C6B62] font-medium mb-2">Three things I want retirement to give me</div>
            {[0, 1, 2].map((i) => (
              <input
                key={i}
                data-testid={`v-thing-${i}`}
                value={w.vision.three_things[i] || ""}
                onChange={(e) => {
                  const next = [...w.vision.three_things];
                  next[i] = e.target.value;
                  set("vision.three_things", next);
                }}
                placeholder={`${i + 1}.`}
                className={inputCls + " mb-2"}
              />
            ))}
          </div>
          <Callout tone="green" icon={TreePine} title="Kitchen Table Conversation™">
            Ask your spouse, partner, or someone close: <em>&ldquo;What does a great retirement look like to you?&rdquo;</em>
            Don&apos;t talk about money first — talk about life.
          </Callout>
          <Field label="What surprised me about the conversation?" value={w.vision.kitchen_table} onChange={(v) => set("vision.kitchen_table", v)} textarea />
        </Part>

        {/* PART 2 — Know where you stand today */}
        <Part n="2" title="Know where you stand today">
          <SubHead>Money I have</SubHead>
          <MoneyGrid data={w.money.have} onChange={(k, v) => set(`money.have.${k}`, v)} fields={[
            ["checking","Checking"],["savings","Savings"],["emergency","Emergency fund"],
            ["k401","401(k) / 403(b)"],["ira","IRAs"],["investments","Investments"],["other","Other"],
          ]} />
          <Totals label="Approximate total" value={moneyHave} testid="tot-have" />

          <SubHead className="mt-8">Money I owe</SubHead>
          <MoneyGrid data={w.money.owe} onChange={(k, v) => set(`money.owe.${k}`, v)} fields={[
            ["mortgage","Mortgage"],["vehicles","Vehicle loans"],["credit_cards","Credit cards"],
            ["student","Student loans"],["personal","Personal loans"],["other","Other"],
          ]} />
          <Totals label="Approximate total debt" value={moneyOwe} testid="tot-owe" />

          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <Field label="My biggest debt concern" value={w.money.debt_biggest_concern} onChange={(v) => set("money.debt_biggest_concern", v)} textarea />
            <Field label="My first action will be" value={w.money.debt_first_action} onChange={(v) => set("money.debt_first_action", v)} textarea />
          </div>
        </Part>

        {/* PART 3 — Monthly money */}
        <Part n="3" title="My monthly money">
          <SubHead>Monthly income</SubHead>
          <MoneyGrid data={w.monthly.income} onChange={(k, v) => set(`monthly.income.${k}`, v)} fields={[
            ["takehome","Take-home pay"],["spouse","Spouse/partner income"],["other","Other income"],
          ]} />
          <Totals label="Income total" value={inc} testid="tot-inc" />

          <SubHead className="mt-8">Essential monthly expenses</SubHead>
          <MoneyGrid data={w.monthly.expense} onChange={(k, v) => set(`monthly.expense.${k}`, v)} fields={[
            ["housing","Housing"],["utilities","Utilities"],["food","Food"],["transport","Transportation"],
            ["insurance","Insurance"],["healthcare","Healthcare"],["debt","Debt payments"],["other","Other"],
          ]} />
          <Totals label="Expense total" value={exp} testid="tot-exp" />

          <div className={`mt-6 rounded-2xl p-5 ${surplus >= 0 ? "bg-[#E8F0EB] border border-[#2B5339]" : "bg-[#FDF2ED] border border-[#C26747]"}`}>
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold mb-1">
              Available for saving, goals, and lifestyle
            </div>
            <div data-testid="tot-surplus" className="font-mono-num text-3xl text-[#1C2B23]">
              ${surplus.toLocaleString()}
            </div>
          </div>

          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            <Field label="Where does my money surprise me?" value={w.monthly.surprising} onChange={(v) => set("monthly.surprising", v)} textarea />
            <Field label="What could I change?" value={w.monthly.could_change} onChange={(v) => set("monthly.could_change", v)} textarea />
            <Field label="What could I redirect?" value={w.monthly.could_redirect} onChange={(v) => set("monthly.could_redirect", v)} textarea />
          </div>
          <Callout tone="amber" icon={Sprout} title="Plant one seed today">
            For seven days, track every dollar you spend. Don&apos;t judge it. Don&apos;t change anything yet.
            Just notice it. Awareness comes before improvement.
          </Callout>
        </Part>

        {/* PART 4 — Emergency toolbox */}
        <Part n="4" title="My emergency toolbox">
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              ["thousand","Could I handle a $1,000 unexpected expense?"],
              ["one_month","One month without income?"],
              ["three","Three months?"],
              ["six","Six months?"],
            ].map(([k, label]) => (
              <YesMaybeNo key={k} label={label} value={w.emergency[k]} onChange={(v) => set(`emergency.${k}`, v)} testid={`em-${k}`} />
            ))}
          </div>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <Field label="My emergency fund today ($)" type="number" value={w.emergency.have} onChange={(v) => set("emergency.have", v)} />
            <Field label="My essential monthly expenses ($)" type="number" value={w.emergency.essentials} onChange={(v) => set("emergency.essentials", v)} />
            <Field label="Emergency-fund goal ($)" type="number" value={w.emergency.goal} onChange={(v) => set("emergency.goal", v)} />
            <Field label="Automatic contribution each payday ($)" type="number" value={w.emergency.automatic} onChange={(v) => set("emergency.automatic", v)} />
          </div>
          <Callout tone="green" icon={TreePine} title="Centennial Coach">
            Your emergency fund isn&apos;t just money. It&apos;s time. Time to think. Time to make a good decision.
            Time to sleep better at night.
          </Callout>
        </Part>

        {/* PART 5 — Retirement toolbox */}
        <Part n="5" title="My retirement toolbox">
          <CheckGroup label="Accounts I currently have" options={[
            "401(k)","403(b)","Traditional IRA","Roth IRA","Pension","Brokerage","HSA","Other","I need to find out",
          ]} selected={w.retirement.accounts} onToggle={(v) => setMulti("retirement.accounts", v)} testidPrefix="ret-acct" />
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <Field label="Current retirement balance ($)" type="number" value={w.retirement.balance} onChange={(v) => set("retirement.balance", v)} />
            <Field label="Monthly contribution ($)" type="number" value={w.retirement.monthly_contrib} onChange={(v) => set("retirement.monthly_contrib", v)} />
            <Field label="Or % of income" type="number" value={w.retirement.pct_income} onChange={(v) => set("retirement.pct_income", v)} />
            <Field label="Employer contribution / match" value={w.retirement.employer_match} onChange={(v) => set("retirement.employer_match", v)} />
          </div>
          <YesMaybeNo label="Am I receiving all of the employer match?" value={w.retirement.full_match} onChange={(v) => set("retirement.full_match", v)} extra="I don't know" testid="ret-match" />
          <Field label="One thing I need to investigate" value={w.retirement.investigate} onChange={(v) => set("retirement.investigate", v)} textarea />
        </Part>

        {/* PART 6 — Social Security */}
        <Part n="6" title="Social Security">
          <div className="space-y-2">
            {[
              ["has_account","I have created / accessed my Social Security account"],
              ["reviewed_earnings","I have reviewed my earnings record"],
              ["knows_estimate","I know my approximate estimated benefit"],
            ].map(([k, label]) => (
              <YesMaybeNo key={k} label={label} value={w.social[k]} onChange={(v) => set(`social.${k}`, v)} yesno testid={`ss-${k}`} />
            ))}
          </div>
          <div className="mt-6 grid sm:grid-cols-3 gap-3">
            <Field label="Est. at earlier age ($/mo)" type="number" value={w.social.est_early} onChange={(v) => set("social.est_early", v)} />
            <Field label="Est. at full retirement ($/mo)" type="number" value={w.social.est_full} onChange={(v) => set("social.est_full", v)} />
            <Field label="Est. at age 70 ($/mo)" type="number" value={w.social.est_70} onChange={(v) => set("social.est_70", v)} />
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <Field label="Planned claiming age" type="number" value={w.social.planned_age} onChange={(v) => set("social.planned_age", v)} />
            <Field label="Why?" value={w.social.why} onChange={(v) => set("social.why", v)} textarea />
          </div>
          <Field label="What information do I still need?" value={w.social.need_info} onChange={(v) => set("social.need_info", v)} textarea />
        </Part>

        {/* PART 7 — Healthcare */}
        <Part n="7" title="Healthcare">
          <SubHead>Before Medicare, coverage could come from:</SubHead>
          <CheckGroup options={[
            "Employer/retiree coverage","Spouse's coverage","Marketplace/private","COBRA","Other","I don't know yet",
          ]} selected={w.healthcare.pre_medicare_source} onToggle={(v) => setMulti("healthcare.pre_medicare_source", v)} testidPrefix="hc-pre" />

          <SubHead className="mt-8">Medicare readiness</SubHead>
          <CheckGroup options={[
            "I know when I become eligible",
            "I know enrollment timing matters",
            "I understand Medicare parts & choices",
            "I have considered prescription coverage",
            "Healthcare is in my retirement budget",
            "I know where to get official Medicare info",
          ]} selected={w.healthcare.medicare_flags} onToggle={(v) => setMulti("healthcare.medicare_flags", v)} testidPrefix="hc-mc" />

          <Field label="My biggest healthcare question" value={w.healthcare.biggest_question} onChange={(v) => set("healthcare.biggest_question", v)} textarea />
        </Part>

        {/* PART 8 — Investing & risk */}
        <Part n="8" title="Investing & risk" intro="You don't need to be an investment expert. But you should understand what your own money is doing.">
          <CheckGroup label="I know…" options={[
            "Where my retirement money is invested",
            "Why I selected those investments",
            "Approximately how much risk I'm taking",
            "That markets can rise and fall",
            "That inflation can reduce purchasing power",
            "Why diversification matters",
            "That my strategy may need to change near retirement",
          ]} selected={w.investing.knows} onToggle={(v) => setMulti("investing.knows", v)} testidPrefix="inv-know" />

          <SubHead className="mt-6">If my account fell significantly in a difficult market, I would probably…</SubHead>
          <RadioRow options={["Panic","Become uncomfortable","Stay with my plan","Want to invest more","Call an adviser","I really don't know"]}
            value={w.investing.reaction} onChange={(v) => set("investing.reaction", v)} testidPrefix="inv-react" />
          <p className="text-xs text-[#5C6B62] mt-3 italic">
            Risk isn&apos;t only about investments — it&apos;s about how you&apos;ll react to them.
          </p>
        </Part>

        {/* PART 9 — Retirement income */}
        <Part n="9" title="Retirement income">
          <SubHead>Expected monthly income sources</SubHead>
          <MoneyGrid data={w.income.sources} onChange={(k, v) => set(`income.sources.${k}`, v)} fields={[
            ["social","Social Security"],["pension","Pension"],["withdrawals","Retirement withdrawals"],
            ["rental","Rental"],["parttime","Part-time work"],["other","Other"],
          ]} />
          <Totals label="Estimated monthly income" value={retIncomeMonthly} testid="tot-ret-inc" />

          <SubHead className="mt-8">Estimated monthly spending</SubHead>
          <MoneyGrid data={w.income.spending} onChange={(k, v) => set(`income.spending.${k}`, v)} fields={[
            ["housing","Housing"],["food","Food"],["transport","Transportation"],["healthcare","Healthcare"],
            ["insurance","Insurance"],["travel","Travel / fun"],["giving","Family / giving"],["other","Other"],
          ]} />
          <Totals label="Estimated monthly need" value={retSpendMonthly} testid="tot-ret-spend" />

          <div className={`mt-6 rounded-2xl p-5 ${retIncomeMonthly - retSpendMonthly >= 0 ? "bg-[#E8F0EB] border border-[#2B5339]" : "bg-[#FDF2ED] border border-[#C26747]"}`}>
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold mb-1">Difference</div>
            <div data-testid="tot-ret-diff" className="font-mono-num text-3xl text-[#1C2B23]">
              ${(retIncomeMonthly - retSpendMonthly).toLocaleString()}
            </div>
          </div>
          <RadioRow label="Does my current plan appear to have…"
            options={["A surplus","A small gap","A significant gap","I need better information"]}
            value={w.income.plan_state} onChange={(v) => set("income.plan_state", v)} testidPrefix="inc-state" />
        </Part>

        {/* PART 10 — Taxes & withdrawals */}
        <Part n="10" title="Taxes & withdrawals">
          <CheckGroup options={[
            "Retirement income may be taxable",
            "I know which accounts are tax-deferred",
            "I know which accounts may be tax-free (qualified)",
            "Withdrawal order can affect taxes",
            "RMD rules may eventually apply",
            "I know when to consult a tax professional",
          ]} selected={w.taxes.knows} onToggle={(v) => setMulti("taxes.knows", v)} testidPrefix="tx-know" />
          <Field label="My biggest tax question" value={w.taxes.biggest_question} onChange={(v) => set("taxes.biggest_question", v)} textarea />
        </Part>

        {/* PART 11 — Protect what you've built */}
        <Part n="11" title="Protect what you've built">
          <CheckGroup label="Family protection check" options={[
            "Beneficiaries reviewed",
            "Will reviewed",
            "Healthcare directive",
            "Financial power of attorney",
            "Passwords / account access organised securely",
            "Insurance reviewed",
            "Important documents organised",
            "Trusted family member knows where information is kept",
            "Fraud / scam protection plan discussed",
          ]} selected={w.estate.checks} onToggle={(v) => setMulti("estate.checks", v)} testidPrefix="est-check" />
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <Field label="Last review date" type="date" value={w.estate.last_review} onChange={(v) => set("estate.last_review", v)} />
            <Field label="Next review date" type="date" value={w.estate.next_review} onChange={(v) => set("estate.next_review", v)} />
          </div>
        </Part>

        {/* PART 12 — Retirement is also a life plan */}
        <Part n="12" title="Retirement is also a life plan"
          intro="Work provides more than a paycheck — routine, friends, purpose, responsibility, conversation, identity. Retirement changes those things.">
          <Field label="On a normal Monday morning, I imagine myself" value={w.life.monday_morning} onChange={(v) => set("life.monday_morning", v)} textarea />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="People I hope to spend more time with" value={w.life.people} onChange={(v) => set("life.people", v)} textarea />
            <Field label="Things I want to learn" value={w.life.learn} onChange={(v) => set("life.learn", v)} textarea />
            <Field label="Ways I want to help others" value={w.life.help} onChange={(v) => set("life.help", v)} textarea />
            <Field label="Places I want to visit" value={w.life.places} onChange={(v) => set("life.places", v)} textarea />
          </div>
          <Field label="Things I've always wanted to do" value={w.life.always_wanted} onChange={(v) => set("life.always_wanted", v)} textarea />
          <SubHead className="mt-6">My retirement bucket list</SubHead>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Do" value={w.life.bucket.do} onChange={(v) => set("life.bucket.do", v)} textarea />
            <Field label="Go" value={w.life.bucket.go} onChange={(v) => set("life.bucket.go", v)} textarea />
            <Field label="Learn" value={w.life.bucket.learn} onChange={(v) => set("life.bucket.learn", v)} textarea />
            <Field label="Share" value={w.life.bucket.share} onChange={(v) => set("life.bucket.share", v)} textarea />
          </div>
        </Part>

        {/* PART 14 — My top 5 */}
        <Part n="14" title="My top five" intro="Don't leave this workbook with 25 things to do. Choose five.">
          <div className="space-y-4">
            {w.actions.map((a, i) => (
              <div key={i} data-testid={`action-${i}`} className="bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl p-4">
                <div className="text-[10px] uppercase tracking-wider text-[#2B5339] font-semibold">#{i + 1}</div>
                <Field label="I need to" value={a.need_to} onChange={(v) => setAction(i, "need_to", v)} />
                <Field label="First step" value={a.first_step} onChange={(v) => setAction(i, "first_step", v)} />
                <Field label="By when" type="date" value={a.by_when} onChange={(v) => setAction(i, "by_when", v)} />
              </div>
            ))}
          </div>
        </Part>

        {/* One-year promise + Future You */}
        <Part n="—" title="My one-year promise & letter to Future Me">
          <Field label="Twelve months from today, I want to be able to say…" value={w.one_year_promise} onChange={(v) => set("one_year_promise", v)} textarea rows={3} />
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <Field label="Signature" value={w.signature} onChange={(v) => set("signature", v)} testid="wb-sign" />
            <Field label="Date" type="date" value={w.signature_date} onChange={(v) => set("signature_date", v)} />
          </div>
          <Callout tone="green" icon={Users} title="Future You™">
            Imagine yourself comfortably retired. Your future self is sitting across the table. They know every decision
            you&apos;re about to make. What would Future You ask you to start doing today?
          </Callout>
          <Field label="Dear Future Me, I'm working on it…" value={w.future_you} onChange={(v) => set("future_you", v)} textarea rows={4} />
        </Part>

        {/* Next step + workshop reflection */}
        <section className="mt-12 print:break-inside-avoid">
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-3">The Workshop</div>
          <blockquote className="border-l-4 border-[#2B5339] pl-6 py-1 text-lg text-[#1C2B23] leading-relaxed italic">
            Some toolboxes may already be full. Some may be half-built. Some may still be empty. That&apos;s okay. You
            don&apos;t build a workshop in one day. You build one toolbox. Put it on the shelf. Then build another.
          </blockquote>
        </section>

        {/* Advisor share link — persists the workbook to a shortlink for read-only sharing */}
        <AdvisorSharePanel workbook={w} />

        {/* CTAs */}
        <div className="mt-10 grid sm:grid-cols-2 gap-4 print:hidden">
          <Link to="/" data-testid="wb-cta-calculator" className="bg-[#1C2B23] text-white rounded-2xl p-6 hover:bg-[#2B5339] transition-colors group">
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-white/60 mb-1">Your next step</div>
            <div className="text-2xl font-medium">Run your projection with these numbers →</div>
            <div className="text-sm text-white/70 mt-2">
              Free · no signup · 100-year Monte Carlo · stress tests included.
            </div>
          </Link>
          <Link to="/learn" data-testid="wb-cta-learn" className="bg-white border border-[#2B5339] text-[#1C2B23] rounded-2xl p-6 hover:bg-[#E8F0EB] transition-colors group">
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-1">Keep learning</div>
            <div className="text-2xl font-medium">All Centennial lessons →</div>
            <div className="text-sm text-[#5C6B62] mt-2">Short 5-minute reads to fill each toolbox.</div>
          </Link>
        </div>

        <p className="mt-10 text-xs text-[#5C6B62] leading-relaxed">
          This workbook is for educational purposes only and does not provide individualized financial, investment, tax,
          legal, Social Security, Medicare, or healthcare advice. Rules, benefits, tax laws, and program requirements can
          change. Verify current information with the appropriate official source and qualified professionals when appropriate.
        </p>
        <p className="mt-4 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339]">
          MyCentennialPlan™ · Learn • Share • Grow
        </p>
      </div>

      <style>{`
        @media print {
          @page { margin: 0.4in; }
          input[type="checkbox"] { -webkit-appearance: checkbox; appearance: checkbox; }
        }
      `}</style>

      {milestone && (
        <MilestoneModal
          milestone={milestone}
          score={total}
          kind="workbook"
          name={w.meta?.name || ""}
          onClose={closeMilestone}
        />
      )}
    </div>
  );
}

// ─────────────────────────── sub-components ─────────────────────────

const inputCls =
  "w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2B5339]";

function Field({ label, value, onChange, type = "text", textarea, rows = 2, testid }) {
  return (
    <label className="block">
      <span className="text-xs text-[#5C6B62] font-medium">{label}</span>
      {textarea ? (
        <textarea
          data-testid={testid}
          rows={rows}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls + " mt-1"}
        />
      ) : (
        <input
          data-testid={testid}
          type={type}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls + " mt-1"}
        />
      )}
    </label>
  );
}

function Part({ n, title, intro, children }) {
  return (
    <section
      data-testid={`part-${n}`}
      className="mt-10 bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8 print:break-inside-avoid"
    >
      <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-1">
        Part {n}
      </div>
      <h2 className="text-2xl sm:text-3xl font-medium text-[#1C2B23]">{title}</h2>
      {intro && <p className="text-sm text-[#5C6B62] mt-2 leading-relaxed">{intro}</p>}
      <div className="mt-5 space-y-4">{children}</div>
    </section>
  );
}

function SubHead({ children, className = "" }) {
  return (
    <div className={`text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C6B62] ${className}`}>
      {children}
    </div>
  );
}

function CheckGroup({ label, options, selected = [], onToggle, testidPrefix }) {
  return (
    <div>
      {label && <SubHead className="mb-2">{label}</SubHead>}
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o}
            data-testid={`${testidPrefix}-${o.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            type="button"
            onClick={() => onToggle(o)}
            className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
              selected.includes(o)
                ? "bg-[#2B5339] border-[#2B5339] text-white"
                : "bg-white border-[#E5E2DC] text-[#5C6B62] hover:border-[#2B5339]"
            }`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

function MoneyGrid({ fields, data, onChange }) {
  return (
    <div className="grid sm:grid-cols-3 gap-3">
      {fields.map(([k, label]) => (
        <label key={k} className="block">
          <span className="text-xs text-[#5C6B62] font-medium">{label}</span>
          <input
            data-testid={`mg-${k}`}
            type="number"
            value={data[k] || ""}
            onChange={(e) => onChange(k, e.target.value)}
            className={inputCls + " mt-1"}
          />
        </label>
      ))}
    </div>
  );
}

function Totals({ label, value, testid }) {
  return (
    <div className="mt-3 bg-[#F0EFEA] rounded-lg px-4 py-3 flex items-center justify-between">
      <span className="text-xs text-[#5C6B62] uppercase tracking-wider font-semibold">{label}</span>
      <span data-testid={testid} className="font-mono-num text-2xl text-[#1C2B23]">
        ${value.toLocaleString()}
      </span>
    </div>
  );
}

function YesMaybeNo({ label, value, onChange, yesno, extra, testid }) {
  const opts = yesno ? ["Yes", "No"] : ["Yes", "Maybe", "No"];
  const options = extra ? [...opts, extra] : opts;
  return (
    <div>
      <div className="text-sm text-[#1C2B23]">{label}</div>
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o}
            data-testid={`${testid}-${o.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            type="button"
            onClick={() => onChange(o)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              value === o
                ? "bg-[#2B5339] border-[#2B5339] text-white"
                : "bg-white border-[#E5E2DC] text-[#5C6B62] hover:border-[#2B5339]"
            }`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

function RadioRow({ label, options, value, onChange, testidPrefix }) {
  return (
    <div>
      {label && <SubHead className="mb-2">{label}</SubHead>}
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o}
            data-testid={`${testidPrefix}-${o.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            type="button"
            onClick={() => onChange(o)}
            className={`text-sm px-4 py-2 rounded-full border transition-colors ${
              value === o
                ? "bg-[#2B5339] border-[#2B5339] text-white"
                : "bg-white border-[#E5E2DC] text-[#5C6B62] hover:border-[#2B5339]"
            }`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

function Callout({ tone = "green", icon: Icon, title, children }) {
  const cls =
    tone === "green"
      ? "bg-[#E8F0EB] border-[#2B5339]"
      : "bg-[#FDF2ED] border-[#C26747]";
  return (
    <div className={`mt-4 rounded-2xl border-2 p-5 ${cls}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-1 inline-flex items-center gap-2">
        {Icon ? <Icon size={12} /> : null} {title}
      </div>
      <p className="text-sm text-[#1C2B23] leading-relaxed">{children}</p>
    </div>
  );
}

function ScorePanel({ total, tier, pct, scores, setScore }) {
  return (
    <section
      data-testid="wb-score-panel"
      className="mt-8 bg-white border-2 border-[#2B5339] rounded-2xl p-6 sm:p-7 print:break-inside-avoid"
    >
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339]">
            My readiness score
          </div>
          <div className="font-mono-num text-5xl sm:text-6xl font-medium text-[#1C2B23] leading-none mt-1">
            <span data-testid="wb-score-value">{total}</span>
            <span className="text-[#5C6B62] text-3xl"> / 100</span>
          </div>
        </div>
        <div className="text-right max-w-md">
          <div className="text-xl font-medium text-[#1C2B23]">{tier.label}</div>
          <p className="text-sm text-[#5C6B62] mt-1 leading-relaxed">{tier.detail}</p>
        </div>
      </div>
      <div className="mt-4 h-2 w-full bg-[#F0EFEA] rounded-full overflow-hidden">
        <div className="h-full bg-[#2B5339] transition-all" style={{ width: `${pct}%` }} />
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-3">
        {TOOLBOXES.map((tb) => (
          <div key={tb.key} className="flex items-center justify-between gap-3 bg-[#F9F8F6] rounded-lg px-3 py-2">
            <span className="text-sm text-[#1C2B23]">{tb.label}</span>
            <div className="flex gap-1">
              {[0, 5, 10].map((n) => (
                <button
                  key={n}
                  data-testid={`sc-${tb.key}-${n}`}
                  type="button"
                  onClick={() => setScore(tb.key, n)}
                  className={`text-xs font-mono-num px-2.5 py-1 rounded border transition-colors ${
                    (scores[tb.key] || 0) === n
                      ? "bg-[#2B5339] border-[#2B5339] text-white"
                      : "bg-white border-[#E5E2DC] text-[#5C6B62] hover:border-[#2B5339]"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-[#5C6B62] mt-3">
        Score each toolbox: <strong>10 = complete</strong>, <strong>5 = partial</strong>, <strong>0 = not yet</strong>.
      </p>
    </section>
  );
}
